/**
 * Swing Trade Dashboard Overlay — MT4-reference two-panel design
 * Left: colored label badges + values  |  Right: symbol, price, P&L
 * Toggle support, arrow badges.
 * CSS prefix: sw-
 */
import React from "react";
import { SwingTradeDashboard } from "../../indicators/built-in/swing-trade";
import "./SwingTradeOverlay.scss";

interface SwingTradeOverlayProps {
  dashboard: SwingTradeDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard?: boolean;
  onToggleDashboard?: () => void;
  arrowBadges?: { x: number; y: number; direction: "long" | "short" }[];
}

const SwingTradeOverlay: React.FC<SwingTradeOverlayProps> = ({
  dashboard, symbol, visible,
  showDashboard = true, onToggleDashboard, arrowBadges = [],
}) => {
  if (!visible || !dashboard) return null;

  const { signal, trend, preset, entry, stopLoss, trailingStop,
    target1, target2, currentPrice, livePL, bookedPL,
    status, tp1Hit, tp2Hit, slHit, tslHit } = dashboard;

  const isLong = trend === "LONG";
  const isShort = trend === "SHORT";
  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "—");
  const fmtPL = (v: number) => (v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2));

  const signalLabel = isShort ? "SELL" : isLong ? "BUY" : "—";
  const themeClass = isLong ? "sw-buy" : isShort ? "sw-sell" : "sw-neutral";

  const rows = [
    { label: signalLabel, value: fmt(entry), cssLabel: "sw-sig-label", cssValue: "", hit: false },
    { label: "TGT-1", value: fmt(target1), cssLabel: "sw-tgt-label-badge", cssValue: tp1Hit ? "sw-hit" : "", hit: tp1Hit },
    { label: "TGT-2", value: fmt(target2), cssLabel: "sw-tgt-label-badge", cssValue: tp2Hit ? "sw-hit" : "", hit: tp2Hit },
    { label: "SL", value: fmt(stopLoss), cssLabel: "sw-sl-label-badge", cssValue: slHit ? "sw-sl-hit" : "", hit: slHit },
    { label: "T-SL", value: fmt(trailingStop), cssLabel: "sw-tsl-label-badge", cssValue: tslHit ? "sw-tsl-hit" : "", hit: tslHit },
  ];

  return (
    <>
      {/* Arrow Badges */}
      {arrowBadges.map((badge, i) => (
        <div
          key={i}
          className={`sw-arrow-badge sw-arrow-badge--${badge.direction}`}
          style={{
            left: badge.x,
            top: badge.direction === "long" ? badge.y + 30 : badge.y - 44,
          }}
        >
          {badge.direction === "long" ? "Buy" : "Sell"}
        </div>
      ))}

      <div className={`sw-overlay ${themeClass}${showDashboard ? "" : " sw-overlay--collapsed"}`}>
        {showDashboard ? (
          <>
          {/* Indicator name header with inline toggle */}
          <div className="sw-name-bar">
            <span className="sw-name-spacer" />
            <span className="sw-name-text">Swing Trade</span>
            <button
              className="sw-toggle-btn"
              onClick={onToggleDashboard}
              title="Hide dashboard"
              aria-label="Hide dashboard"
            >
              <span className="sw-toggle-icon sw-toggle-icon--open">&#x25B6;</span>
            </button>
          </div>
          <div className="sw-body">
            {/* ─── Left Panel: Signal rows ─── */}
            <div className="sw-left">
              {rows.map((r, i) => (
                <div key={i} className="sw-row">
                  <span className={`sw-row-label ${r.cssLabel}`}>{r.label}</span>
                  <span className={`sw-row-value ${r.cssValue}`}>{r.value}</span>
                </div>
              ))}
            </div>

            {/* ─── Right Panel: Symbol + Price + P&L ─── */}
            <div className="sw-right">
              <div className="sw-right-symbol">{symbol}</div>
              <div className="sw-right-price">{currentPrice.toFixed(2)}</div>
              <div className="sw-right-pl">
                <div className="sw-pl-cell">
                  <span className="sw-pl-label">Booked P&amp;L</span>
                  <span className={`sw-pl-value ${bookedPL >= 0 ? "sw-profit" : "sw-loss"}`}>{fmtPL(bookedPL)}</span>
                </div>
                <div className="sw-pl-cell">
                  <span className="sw-pl-label">Live P&amp;L</span>
                  <span className={`sw-pl-value ${livePL >= 0 ? "sw-profit" : "sw-loss"}`}>{fmtPL(livePL)}</span>
                </div>
              </div>
            </div>
          </div>
          </>
        ) : (
          <>
          <div className="sw-collapsed-title">Swing Trade</div>
          <button
            className="sw-toggle-btn"
            onClick={onToggleDashboard}
            title="Show dashboard"
            aria-label="Show dashboard"
          >
            <span className="sw-toggle-icon">&#x25B6;</span>
          </button>
          </>
        )}
      </div>
    </>
  );
};

export default SwingTradeOverlay;
